import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector } from '@angular/core';
import { Element } from '../../equipment/models/elements/element';
import { DetailedElementWidgetComponent } from '../planogram-edit/components/detailed-element-widget/detailed-element-widget.component';
import {
	DETAILED_ELEMENT_WIDGET_DATA,
	DETAILED_ELEMENT_WIDGET_REF,
	DetailedElementWidgetData,
	DetailedElementWidgetRef
} from '../planogram-edit/components/detailed-element-widget/detailed-element-widget.tokens';
import { IPlanogram } from '../models/planogram';

export interface DetailedElementWidgetOverlayOpenParams {
	overlay: Overlay;
	parentInjector: Injector;
	hostContainer?: HTMLElement;
	boundingRect: DOMRect;
	element: Element;
	planogram: IPlanogram;
	afterClose?: () => void;
}

interface OpenedDetailedElement {
	overlay: OverlayRef;
	element: Element;
}

@Injectable()
export class DetailedElementWidgetOverlayService {

	private openedDetailedElements: Array<OpenedDetailedElement> = [];
	private detailedElementZIndex: number = 1000;

	public isOpened(element: Element): boolean {
		return this.openedDetailedElements.some((item: OpenedDetailedElement) => item.element === element);
	}

	public hasOpened(): boolean {
		return this.openedDetailedElements.length > 0;
	}

	public open(params: DetailedElementWidgetOverlayOpenParams): OverlayRef {
		const overlayRef: OverlayRef = params.overlay.create({
			positionStrategy: params.overlay.position().global(),
			hasBackdrop: false,
			panelClass: 'detailed-element-widget-overlay',
			scrollStrategy: params.overlay.scrollStrategies.noop()
		});

		overlayRef.overlayElement.style.left = `${params.boundingRect.left}px`;
		overlayRef.overlayElement.style.top = `${params.boundingRect.top}px`;
		this.attachOverlayToHost(overlayRef, params.hostContainer);

		const widgetData: DetailedElementWidgetData = {
			element: params.element,
			planogram: params.planogram,
			overlayRef: overlayRef,
			boundingRect: params.boundingRect
		};
		const widgetRef: DetailedElementWidgetRef = {
			focus: () => this.setZIndex(overlayRef),
			close: () => {
				this.close(overlayRef);
				params.afterClose?.();
			}
		};
		const injector: Injector = Injector.create({
			parent: params.parentInjector,
			providers: [
				{ provide: DETAILED_ELEMENT_WIDGET_DATA, useValue: widgetData },
				{ provide: DETAILED_ELEMENT_WIDGET_REF, useValue: widgetRef }
			]
		});
		const portal: ComponentPortal<DetailedElementWidgetComponent> = new ComponentPortal(DetailedElementWidgetComponent, null, injector);
		overlayRef.attach(portal);

		this.openedDetailedElements.push({ overlay: overlayRef, element: params.element });
		this.setZIndex(overlayRef);
		return overlayRef;
	}

	public close(overlayRef: OverlayRef): void {
		this.openedDetailedElements = this.openedDetailedElements
			.filter((item: OpenedDetailedElement) => item.overlay !== overlayRef);
		overlayRef.dispose();
		if (!this.openedDetailedElements.length) this.detailedElementZIndex = 1000;
	}

	public closeAll(): void {
		if (!this.openedDetailedElements.length) return;
		this.openedDetailedElements.forEach((item: OpenedDetailedElement) => item.overlay.dispose());
		this.openedDetailedElements = [];
		this.detailedElementZIndex = 1000;
	}

	private attachOverlayToHost(overlayRef: OverlayRef, hostContainer?: HTMLElement): void {
		const overlayHost = overlayRef?.hostElement as HTMLElement | undefined;
		if (!hostContainer || !overlayHost) return;
		if (overlayHost.parentElement !== hostContainer) {
			hostContainer.appendChild(overlayHost);
		}
	}

	private setZIndex(overlayRef: OverlayRef): void {
		const hostEl = overlayRef?.hostElement as HTMLElement;
		if (!hostEl) return;
		this.detailedElementZIndex += 1;
		hostEl.style.zIndex = `${this.detailedElementZIndex}`;
	}
}
